import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export const groupByDriverStint = (laps) => {
  const filtered = laps.filter((l) => l.lap_number !== 1);
  const groups = new Map();
  for (const l of filtered) {
    const key = `${l.driver}|${l.stint}`;
    if (!groups.has(key)) {
      groups.set(key, {
        driver: l.driver,
        stint: l.stint,
        compound: l.compound,
        laps: [],
      });
    }
    groups.get(key).laps.push({
      lapTimeSeconds: Number(l.lap_time_seconds),
      lapNumber: l.lap_number,
    });
  }
  return Array.from(groups.values());
};

// Quali-friendly grouping: one segment per driver so the line connects every
// timed lap regardless of stint changes between fresh-tyre runs.
export const groupByDriver = (laps) => {
  const groups = new Map();
  for (const l of laps) {
    const key = l.driver;
    if (!groups.has(key)) {
      groups.set(key, {
        driver: l.driver,
        stint: 1,
        compound: l.compound,
        laps: [],
      });
    }
    groups.get(key).laps.push({
      lapTimeSeconds: Number(l.lap_time_seconds),
      lapNumber: l.lap_number,
    });
  }
  for (const g of groups.values()) g.laps.sort((a, b) => a.lapNumber - b.lapNumber);
  return Array.from(groups.values());
};

export const useData = (race, session) => {
  const { user, getIdToken } = useAuth();
  const userId = user?.userId ?? null;
  const [state, setState] = useState({ status: 'idle', data: null });

  useEffect(() => {
    if (!race || !session) {
      setState({ status: 'idle', data: null });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading', data: null });
    (async () => {
      try {
        const token = await getIdToken();
        const url = `${API_BASE}/api/laps?race=${encodeURIComponent(race)}&session=${encodeURIComponent(session)}`;
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (cancelled) return;
        const laps = body.laps || [];
        setState({
          status: 'ready',
          data: { laps, groups: groupByDriverStint(laps) },
        });
      } catch (err) {
        console.error('Failed to load laps:', err);
        if (!cancelled) setState({ status: 'error', data: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [race, session, userId, getIdToken]);

  return state;
};
